import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import { restoreBag, cartItemsFromLines } from '../lib/emailLinks';
import PageTitle from '../components/ui/PageTitle';
import './EmailLinkPages.css';

function money(n) {
  return '$' + Number(n || 0).toFixed(2);
}

// /bag/restore/:token - the "Return to your bag" button in abandoned-cart
// emails. Refills the bag with what the customer left, then offers checkout.
// key={token}: a second link opened in the same tab starts fresh.
export default function RestoreBagPage() {
  var token = useParams().token;
  return <RestoreBag key={token} token={token} />;
}

function RestoreBag({ token }) {
  var cart = useCart();
  var productsCtx = useProducts();
  var [reply, setReply] = useState(null);
  var [view, setView] = useState({ state: 'loading' });
  var applied = useRef(false);

  useEffect(function() {
    var cancelled = false;
    restoreBag(token).then(function(r) { if (!cancelled) setReply(r); });
    return function() { cancelled = true; };
  }, [token]);

  // Once both the saved bag and the shop's products have arrived.
  useEffect(function() {
    if (!reply || productsCtx.loading || applied.current) return;
    applied.current = true;
    if (reply.error) { setView({ state: 'error', message: reply.error }); return; }
    if (reply.alreadyPaid) { setView({ state: 'paid' }); return; }
    // The shop's product list didn't load: don't claim the bags sold out.
    if (productsCtx.loadError && reply.items.length) {
      setView({ state: 'error', message: 'The shop couldn’t load its bags just now. Please try the link again in a moment.' });
      return;
    }
    var built = cartItemsFromLines(reply.items, productsCtx.allProducts || []);
    if (built.items.length) cart.restoreItems(built.items);
    setView({ state: built.items.length ? 'ready' : 'empty', items: built.items, missing: built.missing + reply.unavailable });
  }, [reply, productsCtx.loading, productsCtx.loadError, productsCtx.allProducts, cart]);

  var body;
  if (view.state === 'loading') {
    body = (
      <>
        <h1 className="el-heading">Refilling your bag…</h1>
        <p className="el-body">Just a moment.</p>
      </>
    );
  } else if (view.state === 'error') {
    body = (
      <>
        <h1 className="el-heading">We couldn&rsquo;t refill your bag</h1>
        <p className="el-body" role="alert">{view.message}</p>
        <div className="el-actions">
          <Link to="/shop" className="el-btn-primary">Browse the collection</Link>
          <Link to="/contact" className="el-btn-ghost">Contact Andre</Link>
        </div>
      </>
    );
  } else if (view.state === 'paid') {
    body = (
      <>
        <h1 className="el-heading">This order is already paid</h1>
        <p className="el-body">Thank you! Andre is on it. Your confirmation email has the details.</p>
        <div className="el-actions">
          <Link to="/shop" className="el-btn-primary">Browse the collection</Link>
        </div>
      </>
    );
  } else if (view.state === 'empty') {
    body = (
      <>
        <h1 className="el-heading">Those bags aren&rsquo;t available any more</h1>
        <p className="el-body">Everything that was in your bag has sold out or been taken down. Have a look at what&rsquo;s in the collection now.</p>
        <div className="el-actions">
          <Link to="/shop" className="el-btn-primary">Browse the collection</Link>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <h1 className="el-heading">Your bag is ready</h1>
        <p className="el-body">We&rsquo;ve put back what you left. Prices are today&rsquo;s.</p>
        <ul className="el-items" aria-label="Your bag">
          {view.items.map(function(item) {
            var options = [item.variantName, item.colourName].filter(Boolean).join(' · ');
            return (
              <li key={item.key} className="el-item">
                <div>
                  <div className="el-item-name">{item.name}</div>
                  {options && <div className="el-item-opts">{options}</div>}
                </div>
                <div className="el-item-price">{item.quantity > 1 ? item.quantity + ' × ' : ''}{money(item.price)}</div>
              </li>
            );
          })}
        </ul>
        {view.missing > 0 && (
          <p className="el-note">
            {view.missing === 1 ? '1 item from your bag is' : view.missing + ' items from your bag are'} no longer available, so we left {view.missing === 1 ? 'it' : 'them'} out.
          </p>
        )}
        <div className="el-actions">
          <Link to="/checkout" className="el-btn-primary">Continue to checkout</Link>
          <Link to="/shop" className="el-btn-ghost">Keep shopping</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle title="Your bag" />
      <div className="el-page">
        <div className="el-card">{body}</div>
      </div>
    </>
  );
}
